import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Auth } from 'aws-amplify';
import Axios from 'axios';
import domain from 'src/config/domain';
import { API_ROUTES } from 'src/config/api';

import { getRoutes as getDashboardRoutes } from 'src/pages/dashboard';
import { getRoutes as getBookingsRoutes } from 'src/pages/bookings';
import { getRoutes as getCustomersRoutes } from 'src/pages/customers';
import { getRoutes as getPaymentsRoutes } from 'src/pages/payments';
import { getRoutes as getDevelopersRoutes } from 'src/pages/developers';
import { getRoutes as getSettlementRoutes } from 'src/pages/settlement';
import { getRoutes as getOrganisationRoutes } from 'src/pages/organisation';
import { getRoutes as getShieldRoutes } from 'src/pages/shield';
import { getRoutes as getSurchargingRoutes } from 'src/pages/surcharging';
import { getRoutes as getPlaybooksRoutes } from 'src/pages/playbooks';

const ROUTE_SOURCES = [
  { section: 'Dashboard', fn: getDashboardRoutes },
  { section: 'Bookings', fn: getBookingsRoutes },
  { section: 'Customers', fn: getCustomersRoutes },
  { section: 'Payments', fn: getPaymentsRoutes },
  { section: 'Developers', fn: getDevelopersRoutes },
  { section: 'Settlement', fn: getSettlementRoutes },
  { section: 'Organisation', fn: getOrganisationRoutes },
  { section: 'Shield', fn: getShieldRoutes },
  { section: 'Surcharging', fn: getSurchargingRoutes },
  { section: 'Playbooks', fn: getPlaybooksRoutes },
];

const ENTITY_SEARCHES = [
  {
    key: 'bookings',
    label: 'Bookings',
    endpoint: () => API_ROUTES.agent['bookings:get'](),
    mapResult: (item) => ({
      text: item.customer_name || 'Unknown',
      subtitle: item.booking_reference || '',
      link: `/booking/${item.id}`,
    }),
  },
  {
    key: 'customers',
    label: 'Customers',
    endpoint: () => API_ROUTES.agent['customers:get'](),
    mapResult: (item) => ({
      text: item.customer_name || 'Unknown',
      subtitle: item.email || '',
      link: `/customer/${item.id}`,
    }),
  },
  {
    key: 'transactions',
    label: 'Transactions',
    endpoint: () => API_ROUTES.agent['payments:get'](),
    mapResult: (item) => {
      const name = item.booking?.customer_name || item.payment_link?.customer_name || item.metadata?.customer_name || 'Unknown';
      const amount = typeof item.amount === 'number' ? `${(item.amount / 100).toFixed(2)}` : '';
      const status = item.status || '';
      return {
        text: name,
        subtitle: [amount, status].filter(Boolean).join(' - '),
        link: `/payment/${item.id}`,
      };
    },
  },
  {
    key: 'paymentLinks',
    label: 'Payment Links',
    endpoint: () => API_ROUTES.agent['payment-links:get'](),
    mapResult: (item) => {
      const amount = typeof item.amount === 'number' ? `${(item.amount / 100).toFixed(2)}` : '';
      const status = item.status || '';
      return {
        text: item.customer_name || 'Unknown',
        subtitle: [amount, status].filter(Boolean).join(' - '),
        link: `/payment-links/${item.id}`,
      };
    },
  },
];


const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [entityResults, setEntityResults] = useState({});
  const [entityLoading, setEntityLoading] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const abortRef = useRef(null);
  const history = useHistory();
  const domainConfig = domain();

  const { organisation, features } = useSelector((state) => state.organisation);
  const { account } = useSelector((state) => state.account);

  const commands = useMemo(() => {
    if (!account?.roles) return [];

    const results = [];
    for (const source of ROUTE_SOURCES) {
      const routes = source.fn(account, features);
      for (const route of routes) {
        if (route.check) {
          results.push({
            text: route.name,
            link: route.path,
            section: source.section,
            keywords: route.description || '',
          });
        }
      }
    }
    return results;
  }, [account, features]);

  const filtered = useMemo(() => {
    if (!query) return commands;
    const lower = query.toLowerCase();
    return commands.filter(
      (cmd) =>
        cmd.text.toLowerCase().includes(lower) ||
        cmd.keywords.toLowerCase().includes(lower) ||
        cmd.section.toLowerCase().includes(lower)
    );
  }, [query, commands]);

  const grouped = useMemo(() => {
    const groups = {};
    for (const cmd of filtered) {
      const section = cmd.section || 'General';
      if (!groups[section]) groups[section] = [];
      groups[section].push(cmd);
    }
    return groups;
  }, [filtered]);

  // Build flat list combining page results + entity results for keyboard nav
  const flatFiltered = useMemo(() => {
    const result = [];
    for (const section of Object.keys(grouped)) {
      for (const cmd of grouped[section]) {
        result.push(cmd);
      }
    }
    for (const search of ENTITY_SEARCHES) {
      const items = entityResults[search.key];
      if (items && items.length > 0) {
        for (const item of items) {
          result.push(item);
        }
      }
    }
    return result;
  }, [grouped, entityResults]);

  const closePalette = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
    setEntityResults({});
    setEntityLoading(false);
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const navigate = useCallback(
    (link) => {
      closePalette();
      history.push(link);
    },
    [closePalette, history]
  );

  // Debounced entity search
  useEffect(() => {
    if (query.length < 3) {
      setEntityResults({});
      setEntityLoading(false);
      return;
    }

    setEntityLoading(true);

    const timer = setTimeout(async () => {
      // Cancel previous in-flight requests
      if (abortRef.current) {
        abortRef.current.abort();
      }

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const requests = ENTITY_SEARCHES.map((search) =>
          Axios(search.endpoint(), {
            method: 'post',
            data: { keyword: query, organisation, take: 5, skip: 0, date_from: null, date_to: null },
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          })
            .then((res) => ({
              key: search.key,
              items: (res.data.data || []).map(search.mapResult),
            }))
            .catch(() => ({ key: search.key, items: [] }))
        );

        const results = await Promise.all(requests);

        if (!controller.signal.aborted) {
          const mapped = {};
          for (const r of results) {
            mapped[r.key] = r.items;
          }
          setEntityResults(mapped);
          setEntityLoading(false);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setEntityLoading(false);
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [query, organisation]);

  // Listen for Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
        if (open) {
          setQuery('');
          setActiveIndex(0);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Listen for custom event from header button
  useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener('open-command-palette', handleOpen);
    return () => window.removeEventListener('open-command-palette', handleOpen);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Keyboard navigation inside palette
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closePalette();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % flatFiltered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + flatFiltered.length) % flatFiltered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatFiltered[activeIndex]) {
        navigate(flatFiltered[activeIndex].link);
      }
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    if (active) {
      active.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  // Reset active index when query changes
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  if (!open) return null;

  const highlightMatch = (text) => {
    if (!query) return text;
    const lower = text.toLowerCase();
    const idx = lower.indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="font-semibold underline">{text.slice(idx, idx + query.length)}</span>
        {text.slice(idx + query.length)}
      </>
    );
  };

  const hasAnyResults = flatFiltered.length > 0;

  let itemIndex = -1;

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]" onClick={closePalette}>
      <div className="fixed inset-0 bg-black/50" />
      <div
        className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className={`flex items-center px-4 border-b ${domainConfig.styles.lightBorderColour}`}>
          <MagnifyingGlassIcon className={`h-5 w-5 ${domainConfig.styles.textColour} shrink-0`} />
          <input
            ref={inputRef}
            type="text"
            className="w-full py-3 px-3 text-sm bg-transparent outline-none placeholder-gray-400"
            placeholder="Search for anything... (Cmd+K)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-medium text-gray-400 bg-gray-100 rounded border border-gray-200">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-80 overflow-y-auto py-2">
          {!hasAnyResults && !entityLoading && (
            <div className="px-4 py-8 text-center text-sm text-gray-500">No results found</div>
          )}

          {/* Page navigation results */}
          {Object.entries(grouped).map(([section, items]) => (
            <div key={section}>
              {section && (
                <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {section}
                </div>
              )}
              {items.map((cmd) => {
                itemIndex++;
                const isActive = itemIndex === activeIndex;
                const idx = itemIndex;
                return (
                  <button
                    key={cmd.link}
                    data-active={isActive}
                    className={`w-full text-left px-4 py-2 text-sm cursor-pointer flex items-center gap-2 ${
                      isActive
                        ? `${domainConfig.styles.lightBGColour} ${domainConfig.styles.textColour}`
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => navigate(cmd.link)}
                    onMouseEnter={() => setActiveIndex(idx)}
                  >
                    <span>{highlightMatch(cmd.text)}</span>
                  </button>
                );
              })}
            </div>
          ))}

          {/* Entity search results */}
          {ENTITY_SEARCHES.map((search) => {
            const items = entityResults[search.key];
            if (!items || items.length === 0) return null;
            return (
              <div key={search.key}>
                <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {search.label}
                </div>
                {items.map((item) => {
                  itemIndex++;
                  const isActive = itemIndex === activeIndex;
                  const idx = itemIndex;
                  return (
                    <button
                      key={item.link}
                      data-active={isActive}
                      className={`w-full text-left px-4 py-2 text-sm cursor-pointer flex items-center justify-between gap-2 ${
                        isActive
                          ? `${domainConfig.styles.lightBGColour} ${domainConfig.styles.textColour}`
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => navigate(item.link)}
                      onMouseEnter={() => setActiveIndex(idx)}
                    >
                      <span>{highlightMatch(item.text)}</span>
                      {item.subtitle && (
                        <span className="text-xs text-gray-400 truncate ml-2">{item.subtitle}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}

          {/* Loading indicator */}
          {entityLoading && (
            <div className="px-4 py-3 text-center text-xs text-gray-400">
              <svg className="animate-spin h-4 w-4 mx-auto mb-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Searching records...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
